import CustomLoader from "@/components/CustomLoader/customLoader.component";
import DateTimePicker from "@/components/DateTimePicker/dateTimePicker.component";
import TaskPicker from "@/components/TaskPicker/taskPicker.component";
import { useMediaQuery } from "@/hooks/AppContext/mediaQuery";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shadcn/components/ui/dialog";
import { Input } from "@/shadcn/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { NewUserTimer } from "@/types";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type CreateTimerDialogProps = {
  children?: React.ReactNode;
};

const CreateTimerDialog: React.FC<CreateTimerDialogProps> = ({ children }) => {
  const {
    initialGetUserTasks,
    getPrecacheTasks,
    sendNewTimerStart,
    fetchPageTimers,
    fetchEveryoneTimers,
  } = useContext(AppContext);
  const isNotSmall = useMediaQuery("sm");
  const [newTimer, setNewTimer] = useState<NewUserTimer>({
    name: "",
    task_id: null,
    events: [],
  });
  const [newEvent, setNewEvent] = useState<{
    eventStart: Date | null | undefined;
    eventEnd: Date | undefined | null;
  }>({ eventStart: null, eventEnd: null });
  const [creationState, setCreationState] = useState<
    "creation" | "submitting" | "success" | "failed"
  >("creation");
  const [isOpen, setIsOpen] = useState(false);
  const controllerRef = useRef(new AbortController());
  const createDateTimeStartOpen = useRef<boolean>(false);
  const createDateTimeEndOpen = useRef<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // useEffect(() => {
  //   console.log(newTimer);
  // }, [newTimer]);

  function handleEditField(field: keyof NewUserTimer, value: string) {
    setNewTimer((prev) => ({ ...prev, [field]: value }));
  }

  function handleTaskID(id: number) {
    setNewTimer((prev) => ({
      ...prev,
      task_id: prev.task_id === id ? null : id,
    }));
  }

  const validateNewTimerEvent = useCallback(() => {
    setValidationErrors([]);
    const errors: string[] = [];

    if (newEvent.eventEnd) {
      if (!newEvent.eventStart) {
        errors.push("Start time is required.");
      } else if (newEvent.eventEnd <= newEvent.eventStart) {
        errors.push("End time must be after start time.");
      }
    }

    if (errors.length === 0) {
      return;
    }

    console.error("Validation errors:", errors);
    setValidationErrors(errors);
    return;
  }, [newEvent]);

  useEffect(() => {
    validateNewTimerEvent();
  }, [newEvent, validateNewTimerEvent]);

  async function handleSubmit() {
    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;

    setCreationState("submitting");

    const response = await sendNewTimerStart({
      taskID: newTimer.task_id,
      timerName: newTimer.name,
      startTime: newEvent.eventStart,
      endTime: newEvent.eventEnd,
    });

    if (!response) {
      genericFailureToast("Failed to create timer");
      setCreationState("failed");
      return;
    }

    console.log("Timer created successfully");
    setCreationState("success");
    genericSuccessToast("Timer created successfully");
    fetchPageTimers(signal, fetchEveryoneTimers);
    await closeDialog();
    await initialGetUserTasks({ force: true });
    await getPrecacheTasks(false, true);
  }

  async function closeDialog() {
    clearState(false);
  }

  function clearState(open: boolean) {
    setCreationState("creation");
    setNewTimer({
      name: "",
      task_id: null,
      events: [],
    });
    setNewEvent({ eventStart: null, eventEnd: null });
    setIsOpen(open);
  }

  const handleFieldDateTime = (
    type: "start" | "end",
    date: Date | undefined | null
  ) => {
    // console.log("handleFieldDateTime", type, date);
    if (type === "start") {
      setNewEvent((prev) => ({
        eventStart: date,
        eventEnd: date ? prev.eventEnd : null,
      }));
    } else {
      setNewEvent((prev) => ({ ...prev, eventEnd: date }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={clearState}>
      <DialogDescription className="hidden">
        Dialog to create a timer
      </DialogDescription>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="sm:max-w-[500px] !rounded-sm"
        onInteractOutside={(e) => {
          if (creationState === "creation" || creationState === "submitting") {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-center">Create Timer</DialogTitle>
        </DialogHeader>
        {creationState === "creation" ? (
          <>
            <div className="grid grid-cols-[1fr_2fr] gap-4 w-full items-center">
              <label
                className="justify-self-end mr-2"
                htmlFor="create-timer-title"
              >
                Name
              </label>
              <Input
                id="create-timer-title"
                value={newTimer.name}
                onChange={(e) => handleEditField("name", e.target.value)}
                className="truncate"
                maxLength={255}
              />
              <label
                className="justify-self-end mr-2"
                htmlFor="create-timer-task"
              >
                Task
              </label>
              <TaskPicker
                type={"User"}
                taskID={newTimer.task_id}
                handleFieldTask={(id: number) => handleTaskID(id)}
                side={isNotSmall ? "right" : undefined}
                align={isNotSmall ? "start" : undefined}
                triggerButtonClassName="!max-w-none w-full"
                triggerContainerClassName="w-full overflow-hidden"
              />
              <label
                className="justify-self-end mr-2"
                htmlFor="create-timer-start-time"
              >
                Start Time
              </label>
              <DateTimePicker
                dateTime={newEvent?.eventStart}
                onDateTimeChange={(date) => {
                  handleFieldDateTime("start", date);
                }}
                modal={true}
                createDateTimeOpen={createDateTimeStartOpen}
                showTriggerTime={true}
                showTriggerSeconds={true}
              />
              {newEvent.eventStart && (
                <>
                  <label
                    className="justify-self-end mr-2"
                    htmlFor="create-timer-end-time"
                  >
                    End Time
                  </label>
                  <DateTimePicker
                    dateTime={newEvent?.eventEnd}
                    onDateTimeChange={(date) => {
                      handleFieldDateTime("end", date);
                    }}
                    modal={true}
                    createDateTimeOpen={createDateTimeEndOpen}
                    showTriggerTime={true}
                    showTriggerSeconds={true}
                  />
                </>
              )}
            </div>
            <DialogFooter>
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={validationErrors.length > 0}
                        className="justify-self-end"
                      >
                        Create Timer
                      </Button>
                      {validationErrors.length > 0 && (
                        <TooltipContent side="left" className="mr-3">
                          {validationErrors.map((error, index) => (
                            <div key={index}>{error}</div>
                          ))}
                        </TooltipContent>
                      )}
                    </div>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>
            </DialogFooter>
          </>
        ) : creationState === "submitting" ? (
          <CustomLoader size="sm" />
        ) : creationState === "success" ? (
          <DialogDescription>Success</DialogDescription>
        ) : (
          <DialogDescription>Failed</DialogDescription>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateTimerDialog;
